import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css } from 'emotion';
import { omitBy, sortBy, take } from 'lodash';
import { action, computed, extendObservable, IObservableArray, observable, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import iconAdd from '../assets/magicBuilder/iconAdd.svg';
import iconRemove from '../assets/magicBuilder/iconRemove.svg';
import { BREAKPOINT_MEDIUM } from '../consts/breakpoints';
import { I18N_TOKEN } from '../services/InjectTokens';
import { Element } from '../state/models/Element';
import { Button } from './Button';
import { SmallInput } from './SmallInput';

const main = css`
  h3 {
    margin-bottom: 15px;
  }

  p {
    margin: 0;
  }
`;

const activePersons = css`
  white-space: nowrap;
  overflow-x: auto;
  margin-bottom: 20px;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    display: flex;
    flex-wrap: wrap;
    white-space: initial;
  }
`;

const hover = css`
  position: absolute;
  top: 0;
  left: auto;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;

  cursor: pointer;

  transform: translate(50%, -50%);

  img {
    width: 100%;
    height: 100%;
  }

  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    top: 0;
    left: 0;
    right: 0;
    display: none;

    width: 100%;
    height: 100%;

    background: rgba(255, 255, 255, 0.4);
    transform: none;

    img {
      width: auto;
      height: auto;
    }
  }
`;

const activePerson = css`
  display: inline-block;
  position: relative;
  width: 146px;
  margin-top: 20px;

  &:not(:last-child) {
    margin-right: 40px;
  }
`;

const personImage = css`
  position: relative;
  width: 100%;
  height: 193px;

  border-radius: 10px;

  box-shadow: 2px 2px 4px 0 #f1f2f2;
  border: solid 1px #bfbcbc;

  &:hover {
    div[role='button'] {
      display: flex;
    }
  }
`;

const imageWrapper = css`
  display: flex;
  align-items: center;

  width: 100%;
  height: 100%;

  overflow: hidden;
  border-radius: 10px;

  img {
    width: 100%;
    height: auto;
  }
`;

const personInput = css`
  width: 100%;
  margin-top: 15px;
`;

const inactivePersons = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const inactivePerson = css`
  position: relative;

  display: flex;
  align-items: center;

  width: 69px;
  height: 90px;

  margin-top: 20px;

  border-radius: 10px;
  box-shadow: 2px 2px 4px 0 #f1f2f2;
  border: solid 1px #bfbcbc;

  &:not(:last-child) {
    margin-right: 17px;
  }

  &:hover {
    div[role='button'] {
      display: flex;
    }
  }
`;

const buttonContainer = css`
  width: 100%;

  display: flex;
  justify-content: center;

  margin-top: 37px;

  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    justify-content: flex-end;
  }
`;

interface IProps {
  elements: Array<Element>;
  params: Record<string, string>;
  onPersonsSave(params: Record<string, string>): void;
}

interface IComponentState {
  activePersons: IObservableArray<string>;
  params: Record<string, string>;
  isMobile: boolean;
}

@observer
export class MagicBuilderPersons extends React.Component<IProps> {
  private i18n = inject(I18N_TOKEN);
  private wrapperRef: HTMLDivElement | null = null;

  @computed
  private get elements() {
    return sortBy(this.props.elements, ({ uid }) => parseInt(uid.replace(/[^0-9]/g, ''), 10));
  }

  @observable
  private componentState: IComponentState = {
    activePersons: observable.array(),
    isMobile: false,
    params: {},
  };

  constructor(props: IProps) {
    super(props);
    const params = this.elements.reduce(
      (acc, field) => ({
        ...acc,
        [field.uid]: props.params ? props.params[field.uid] : '',
      }),
      {},
    );

    extendObservable(this.componentState.params, params);

    // sets persons as active if they have a name filled in, otherwise
    // prepare 3
    const filledInPersons = Object.keys(omitBy(params, (value) => !value));
    if (filledInPersons.length) {
      set(this.componentState.activePersons, filledInPersons);
    } else {
      const placeholders = take(Object.keys(params), 3);
      set(this.componentState.activePersons, placeholders);
    }
  }

  public componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  @computed
  private get inactivePersons() {
    return this.elements.filter(({ uid }) => !this.componentState.activePersons.includes(uid));
  }

  @computed
  private get activePersons() {
    return this.elements.filter(({ uid }) => this.componentState.activePersons.includes(uid));
  }

  public render() {
    return (
      <div className={main}>
        <h3>{this.i18n.t('special_persons_title')}</h3>
        <p>{this.i18n.t('special_persons_description')}</p>
        <div className={activePersons} ref={(ref) => (this.wrapperRef = ref)}>
          {this.activePersons.map((person) => (
            <div className={activePerson} key={person.uid} id={person.uid}>
              <div className={personImage}>
                <div className={imageWrapper}>
                  <img src={person.image.smallUrl} alt={this.i18n.t(person.uid)} />
                </div>
                <div role="button" className={hover} onClick={this.removeMagic(person)}>
                  <img src={iconRemove} alt="Remove" />
                </div>
              </div>
              <SmallInput
                name={this.i18n.t(person.uid)}
                value={this.componentState.params[person.uid]}
                className={personInput}
                onChange={this.onChange(person.uid)}
                maxLength={person.maxLength}
              />
            </div>
          ))}
        </div>
        {this.inactivePersons.length > 0 && (
          <React.Fragment>
            <p>{this.i18n.t('special_persons_add_more')}</p>
            <div className={inactivePersons}>
              {this.inactivePersons.map((person) => (
                <div className={inactivePerson} key={person.uid}>
                  <div className={imageWrapper}>
                    <img src={person.image.smallUrl} alt={this.i18n.t(person.uid)} />
                  </div>
                  <div role="button" className={hover} onClick={this.addPerson(person)}>
                    <img src={iconAdd} alt="Add" />
                  </div>
                </div>
              ))}
            </div>
          </React.Fragment>
        )}
        <div className={buttonContainer}>
          <Button value={this.i18n.t('save')} onClick={this.onSave} />
        </div>
      </div>
    );
  }

  @bind
  @action
  private onResize() {
    if (window.innerWidth < 992) {
      this.componentState.isMobile = true;
    } else {
      this.componentState.isMobile = false;
    }
  }

  private scrollToElement(uid: string) {
    if (this.wrapperRef) {
      this.forceUpdate();
      const element: any = [...Array.from(this.wrapperRef.childNodes)].find((node: any) => node.id === uid);
      if (element) {
        this.wrapperRef.scrollLeft = element.offsetLeft - 20;
      }
    }
  }

  @action
  private addPerson(person: Element) {
    return () => {
      this.componentState.activePersons.push(person.uid);
      this.forceUpdate(() => {
        this.scrollToElement(person.uid);
      });
    };
  }

  @action
  private removeMagic(person: Element) {
    return () => {
      this.componentState.params[person.uid] = '';
      this.componentState.activePersons.remove(person.uid);
    };
  }

  @bind
  @action
  private onSave() {
    const params = omitBy(this.componentState.params, (value) => !value);
    this.props.onPersonsSave(params);
  }

  @bind
  @action
  private onChange(uid: string) {
    return (e: React.ChangeEvent<HTMLInputElement>) => set(this.componentState.params, uid, e.target.value);
  }
}
